//  ----------      START   IMPORT  BLOCK       ----------
import axios from 'axios';
import type { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import apiClient from './apiClient';
//  ----------      END   IMPORT  BLOCK       ----------

/**
 * @class APIService
 * @description Base class providing abstracted HTTP methods. 
 * Intended to be extended by specific domain services.
 */
export default class APIService {
  // Define the property type for the class
  protected api: AxiosInstance;

  /**
   * @param {AxiosInstance} [customClient] - Optional custom axios instance.
   */
  constructor(customClient: AxiosInstance | null = null) {
    this.api = customClient || apiClient;
  }

  /**
   * Sends a POST request.
   * @template T - The expected response data type
   * @template D - The request payload type
   */
  async post<T = any, D = any>(route: string, dataToSend: D): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(route, dataToSend);
    return response.data;
  }

  /**
   * Sends a PUT request.
   */
  async put<T = any, D = any>(route: string, dataToSend: D): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(route, dataToSend);
    return response.data;
  }

  /**
   * Sends a PATCH request.
   */
  async patch<T = any, D = any>(route: string, dataToSend: D): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(route, dataToSend);
    return response.data;
  }

  /**
   * Sends a GET request with optional cancellation support.
   */
  async get<T = any>(route: string, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.get(route, options);
      return response.data;
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        console.warn('Request canceled.');
      }
      throw error;
    }
  }

  /**
   * Sends a DELETE request to a specific resource ID.
   */
  async delete<T = any>(route: string, entry_id: string | number): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(`${route}/${entry_id}`); 
    return response.data;
  }
}